import { type AdminClient } from '@apx/admin-sdk';
import { renderTimingPage } from './timing.js';
import { renderJackpotPage } from './jackpot.js';
import { renderBigWinnerPage } from './big-winner.js';
import { renderLeaderboardPage } from './leaderboard.js';
import { renderRulesPage } from './rules.js';
import { renderInspectorPage } from './inspector.js';
import { renderAbusePage } from './abuse.js';
import { renderCategoryBonusPage } from './category-bonus.js';
import { renderStreakPage } from './streak.js';
import { renderTableLimitsPage } from './table-limits.js';
import { renderSicboJackpotPage } from './sicbo-jackpot.js';
import { renderRtpPage } from './rtp.js';
import { aliasRtpTab } from './rtp-descriptor.js';

export interface GameDetailDeps {
  client: AdminClient;
  integratorId: string;
  gameId: string;
  roundEngineBaseUrl: string;
  gatewayUrl: string;
  isProviderScope: boolean;
  integratorLabel?: string;
  /** Permission probe from /v1/me. Tabs the principal cannot write are hidden. */
  can: (permission: string) => boolean;
}

type Tab =
  | 'timing'
  | 'rtp'
  | 'table'
  | 'jackpot'
  | 'big-winner'
  | 'leaderboard'
  | 'rules'
  | 'abuse'
  | 'inspector'
  | 'category-bonus'
  | 'streak';

const ALL_TABS: Array<{ id: Tab; label: string }> = [
  { id: 'timing', label: 'Phase timing' },
  { id: 'rtp', label: 'RTP & Payouts' },
  { id: 'table', label: 'Table limits' },
  { id: 'jackpot', label: 'Jackpot' },
  { id: 'big-winner', label: 'Big winner' },
  { id: 'leaderboard', label: 'Leaderboard' },
  { id: 'rules', label: 'Rules' },
  { id: 'abuse', label: 'Anti-abuse' },
  { id: 'category-bonus', label: 'Category bonus' },
  { id: 'streak', label: 'Streak bonus' },
  { id: 'inspector', label: 'Live inspector' },
];

// Per-game tab whitelist. `category-bonus` / `streak` are greedyking-only payout
// surfaces. sicbo has its own jackpot shape; dragontiger has no jackpot pool at
// all. Games not listed fall back to the full set (preserves greedyking + any
// future game).
const TABS_BY_GAME: Record<string, Tab[]> = {
  sicbo: [
    'timing',
    'rtp',
    'table',
    'jackpot',
    'big-winner',
    'leaderboard',
    'rules',
    'abuse',
    'inspector',
  ],
  dragontiger: [
    'timing',
    'rtp',
    'table',
    'big-winner',
    'leaderboard',
    'rules',
    'abuse',
    'inspector',
  ],
  // Flat-paytable digit lottery: no jackpot pool, no RNG weights (uniform 1/10
  // per digit), no category-bonus/streak/greedyking-multipliers surfaces. Scoped
  // like dragontiger (no jackpot). Its tunable payouts surface through the generic
  // RTP tab via the singaporelottery rtp-descriptor entry.
  singaporelottery: [
    'timing',
    'rtp',
    'table',
    'big-winner',
    'leaderboard',
    'rules',
    'abuse',
    'inspector',
  ],
  // Card game with a fixed uniform draw and fixed drawing rules: no jackpot pool
  // and no RNG-weight lever. Its only tunable RTP surface is the payout
  // multipliers (tie odds, pair odds, banker commission), which reach the
  // generic RTP tab through the baccarat rtp-descriptor entry.
  baccarat: ['timing', 'rtp', 'table', 'big-winner', 'leaderboard', 'rules', 'abuse', 'inspector'],
  // Both Dice Rush tables: no jackpot pool, so no jackpot tab. The RTP tab is
  // the generic one, driven by their rtp-descriptor entries.
  shortdicerush: [
    'timing',
    'rtp',
    'table',
    'big-winner',
    'leaderboard',
    'rules',
    'abuse',
    'inspector',
  ],
  longdicerush: [
    'timing',
    'rtp',
    'table',
    'big-winner',
    'leaderboard',
    'rules',
    'abuse',
    'inspector',
  ],
  // Player-picked multiplier from one seeded draw: no jackpot pool, no RNG
  // weights, no category-bonus/streak. Without this entry crash fell back to
  // the full set and rendered greedyking's jackpot and bonus editors. Its one
  // tunable, the RTP constant, lives on the RTP tab via the crash descriptor.
  crash: ['timing', 'rtp', 'table', 'big-winner', 'leaderboard', 'rules', 'abuse', 'inspector'],
};

/**
 * Capability each tab's surface requires, mirroring SURFACE_PERMISSION in
 * admin-api. A tenant admin holds neither GAME_FAIRNESS_WRITE nor GAME_PAUSE,
 * so the odds-model tabs disappear rather than being offered and then 403ing
 * on save — and the RNG weights behind them are the provider's model, not
 * something a tenant should be reading either.
 *
 * Cosmetic: the server refuses these regardless. This stops the console
 * advertising controls it will not honour.
 */
const TAB_PERMISSION: Record<Tab, string> = {
  timing: 'game.fairness.write',
  rtp: 'game.fairness.write',
  jackpot: 'game.fairness.write',
  abuse: 'game.fairness.write',
  table: 'game.bet_limits.write',
  'category-bonus': 'game.bet_limits.write',
  'big-winner': 'game.content.write',
  leaderboard: 'game.content.write',
  rules: 'game.content.write',
  streak: 'game.content.write',
  // Read-only live view of the running round.
  inspector: 'game.config.read',
};

function tabsForGame(gameId: string): Array<{ id: Tab; label: string }> {
  const allow = TABS_BY_GAME[gameId];
  if (!allow) return ALL_TABS;
  const order = new Set(allow);
  return ALL_TABS.filter((t) => order.has(t.id));
}

const DEFAULT_TAB: Tab = 'timing';

export async function renderGameDetailPage(
  root: HTMLElement,
  deps: GameDetailDeps,
  tab: Tab | 'rng' | 'multipliers' | null,
): Promise<void> {
  const tabs = tabsForGame(deps.gameId).filter((t) => deps.can(TAB_PERMISSION[t.id]));
  const allowed = new Set(tabs.map((t) => t.id));
  // If the requested tab isn't valid for this game (e.g. /dragontiger/jackpot),
  // fall back to the first tab this game actually supports.
  const aliased = aliasRtpTab(tab ?? DEFAULT_TAB) as Tab;
  const requested: Tab = aliased;
  const active: Tab = allowed.has(requested) ? requested : (tabs[0]?.id ?? DEFAULT_TAB);
  // Normalize retired deep links (…/rng, …/multipliers) so refresh/bookmark stick.
  if ((tab === 'rng' || tab === 'multipliers') && typeof history !== 'undefined') {
    history.replaceState(null, '', `/games/${encodeURIComponent(deps.gameId)}/rtp`);
  }
  root.innerHTML = `
    <div class="sticky-head">
      <div class="row" style="justify-content:space-between;margin-top:0;margin-bottom:8px">
        <h1 class="page-title" style="margin:0">
          <a href="/games" style="color:var(--text-dim);text-decoration:none">Games</a>
          <span style="color:var(--text-dim)">/</span>
          ${escapeHtml(deps.gameId)}
        </h1>
      </div>
      ${
        deps.isProviderScope
          ? `<div class="global-config-banner">
             <strong>Provider default.</strong>
             Baseline for <em>every integrator</em> that hasn't overridden a value. Two-admin approval; super-admin only.
           </div>`
          : `<div class="global-config-banner" style="background:linear-gradient(90deg,rgba(245,197,66,0.14),transparent)">
             <strong>Editing: ${escapeHtml(deps.integratorLabel ?? deps.integratorId)}.</strong>
             Overrides apply to this integrator only; unset values inherit the provider default.
           </div>`
      }
      <div class="game-tabs">
        ${tabs
          .map(
            (t) =>
              `<a href="/games/${encodeURIComponent(deps.gameId)}/${t.id}" data-tab="${t.id}" class="game-tab ${t.id === active ? 'active' : ''}">${escapeHtml(t.label)}</a>`,
          )
          .join('')}
      </div>
    </div>
    <div id="game-tab-body"></div>
  `;
  const body = root.querySelector<HTMLElement>('#game-tab-body')!;

  switch (active) {
    case 'timing':
      await renderTimingPage(body, {
        client: deps.client,
        integratorId: deps.integratorId,
        gameId: deps.gameId,
        roundEngineBaseUrl: deps.roundEngineBaseUrl,
        isProviderScope: deps.isProviderScope,
      });
      break;
    case 'rtp':
      await renderRtpPage(body, {
        client: deps.client,
        integratorId: deps.integratorId,
        gameId: deps.gameId,
        roundEngineBaseUrl: deps.roundEngineBaseUrl,
        isProviderScope: deps.isProviderScope,
      });
      break;
    case 'jackpot':
      if (deps.gameId === 'sicbo') {
        await renderSicboJackpotPage(body, {
          client: deps.client,
          integratorId: deps.integratorId,
          gameId: deps.gameId,
          roundEngineBaseUrl: deps.roundEngineBaseUrl,
        });
      } else if (deps.gameId === 'dragontiger') {
        body.innerHTML =
          '<div class="card"><p class="muted">Dragon &amp; Tiger has no jackpot pool — payouts are fixed-odds with a weighted crit multiplier (see RNG &amp; RTP).</p></div>';
      } else {
        await renderJackpotPage(body, {
          client: deps.client,
          integratorId: deps.integratorId,
          gameId: deps.gameId,
          roundEngineBaseUrl: deps.roundEngineBaseUrl,
        });
      }
      break;
    case 'big-winner':
      await renderBigWinnerPage(body, {
        client: deps.client,
        integratorId: deps.integratorId,
        gameId: deps.gameId,
      });
      break;
    case 'leaderboard':
      await renderLeaderboardPage(body, {
        client: deps.client,
        integratorId: deps.integratorId,
        gameId: deps.gameId,
        roundEngineBaseUrl: deps.roundEngineBaseUrl,
      });
      break;
    case 'rules':
      await renderRulesPage(body, {
        client: deps.client,
        integratorId: deps.integratorId,
        gameId: deps.gameId,
        roundEngineBaseUrl: deps.roundEngineBaseUrl,
      });
      break;
    case 'abuse':
      await renderAbusePage(body, {
        client: deps.client,
        integratorId: deps.integratorId,
        gameId: deps.gameId,
      });
      break;
    case 'category-bonus':
      await renderCategoryBonusPage(body, {
        client: deps.client,
        integratorId: deps.integratorId,
        gameId: deps.gameId,
      });
      break;
    case 'streak':
      await renderStreakPage(body, {
        client: deps.client,
        integratorId: deps.integratorId,
        gameId: deps.gameId,
      });
      break;
    case 'table':
      await renderTableLimitsPage(body, {
        client: deps.client,
        integratorId: deps.integratorId,
        gameId: deps.gameId,
        isProviderScope: deps.isProviderScope,
      });
      break;
    case 'inspector':
      await renderInspectorPage(body, {
        client: deps.client,
        integratorId: deps.integratorId,
        gameId: deps.gameId,
        gatewayUrl: deps.gatewayUrl,
        roundEngineBaseUrl: deps.roundEngineBaseUrl,
      });
      break;
    default:
      body.innerHTML = '<p class="error">Unknown tab.</p>';
  }
}

export function parseTabFromHash(rest: string): Tab | null {
  const valid: string[] = [
    'timing',
    'rtp',
    'table',
    'jackpot',
    'big-winner',
    'leaderboard',
    'rules',
    'abuse',
    'category-bonus',
    'streak',
    'inspector',
    // Retired ids kept parseable so renderGameDetailPage can alias them to rtp.
    'rng',
    'multipliers',
  ];
  return valid.includes(rest) ? (rest as Tab) : null;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      case "'":
        return '&#39;';
      default:
        return c;
    }
  });
}
